import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { coastalFusionPoints } from "../mock";
import { Anchor, Sparkles, Heart } from "lucide-react";

function PageHeader({ title, subtitle, image }) {
  return (
    <section
      className="relative h-[60vh] min-h-[420px] flex items-center justify-center text-center bg-cover bg-center"
      style={{ backgroundImage: `linear-gradient(rgba(10,36,36,0.7), rgba(10,36,36,0.85)), url(${image})` }}
    >
      <div className="absolute inset-0 grain-overlay" />
      <div className="relative pt-20">
        <span className="divider-ornament text-spice-gold">{subtitle}</span>
        <h1 className="font-display text-white text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight mt-5">
          {title}
        </h1>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <>
      <PageHeader
        title="Our Story"
        subtitle="About Sea Spice Kitchen"
        image="/image/siteimages/about/photo-1517248135467-4c7edcad34c4.jpg"
      />

      {/* Story */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <span className="divider-ornament">Welcome</span>
            <h2 className="font-display text-sea-deep text-4xl md:text-5xl font-medium mt-5 mb-6 leading-[1.1]">
              A celebration of <em className="italic text-spice-gold font-light">the ocean</em>
            </h2>
            <p className="text-sea-mid/85 text-base md:text-lg leading-relaxed mb-5">
              At Sea Spice Kitchen, we bring together the best of the world's coastal cuisines to create a unique dining experience we proudly call <strong className="text-sea-deep">Coastal Fusion</strong>.
            </p>
            <p className="text-sea-mid/85 text-base leading-relaxed mb-5">
              Nestled in the heart of Frankston, our menu celebrates the bounty of the ocean, combining premium Australian seafood with vibrant flavours inspired by the shores of India, the Mediterranean, and beyond. Every dish is thoughtfully crafted using fresh local ingredients, aromatic herbs, and carefully selected spices to create bold yet balanced flavours.
            </p>
            <p className="text-sea-mid/85 text-base leading-relaxed">
              Sea Spice Kitchen is more than a restaurant — it's a destination where friends and family gather to enjoy exceptional food, warm service, and memorable moments by the coast.
            </p>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-spice-gold hidden lg:block" />
            <img
              src="/image/siteimages/about/photo-1759027044799-5aed5577f864.jpg"
              alt="Chef plating coastal fusion dish"
              className="relative w-full h-[560px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* What is Coastal Fusion */}
      <section className="py-24 lg:py-32 bg-sand">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="divider-ornament">Our Philosophy</span>
            <h2 className="font-display text-sea-deep text-4xl md:text-5xl lg:text-6xl font-medium mt-5 mb-5 leading-[1.1]">
              What is <em className="italic text-spice-gold font-light">Coastal Fusion?</em>
            </h2>
            <p className="text-sea-mid/85 text-base md:text-lg">
              Coastal Fusion is the art of combining fresh seafood and coastal ingredients with flavours from around the world.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {coastalFusionPoints.map((p, i) => (
              <div key={p.title} className="bg-white p-8 border-t-2 border-spice-gold hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="font-display text-sea-deep/30 text-5xl mb-2">0{i + 1}</div>
                <h3 className="font-display text-sea-deep text-xl mb-3">{p.title}</h3>
                <p className="text-sea-mid/75 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 lg:py-32 bg-[#0d2929] text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid md:grid-cols-2 gap-10">
          {[
            { icon: <Heart size={28} />, title: "Our Mission", text: "To create exceptional Coastal Fusion dining experiences by combining the freshest seafood, premium ingredients, and globally inspired flavours in a warm and welcoming environment." },
            { icon: <Sparkles size={28} />, title: "Our Vision", text: "To become Victoria's most loved Coastal Fusion dining destination, recognised for culinary excellence, outstanding hospitality, and innovative seafood-inspired cuisine." }
          ].map((item) => (
            <div key={item.title} className="border border-white/10 p-10 lg:p-12 bg-white/5">
              <div className="text-spice-gold mb-5">{item.icon}</div>
              <h3 className="font-display text-3xl md:text-4xl mb-5">{item.title}</h3>
              <p className="text-white/75 leading-relaxed text-base md:text-lg">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-cream text-center">
        <div className="max-w-3xl mx-auto px-5">
          <Anchor className="mx-auto text-spice-gold mb-5" size={32} />
          <h3 className="font-display text-sea-deep text-3xl md:text-4xl mb-5">
            Ready to dine with us?
          </h3>
          <p className="text-sea-mid/80 mb-8">Reserve your table and experience Coastal Fusion at its finest.</p>
          <Link to="/reservations">
            <Button className="bg-spice-gold hover:bg-[hsl(38_75%_46%)] text-[#0d2929] font-semibold rounded-none px-10 h-14">
              Book Your Table
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
}
